import type { Lesson } from "@/core/types";
import { criterion, fromCriteria, unsupported } from "@/core/check";
import { countTrackedChanges } from "@/office/word/review";

export const lektionVergleichen: Lesson = {
  id: "dokumente-vergleichen",
  title: "Zwei Fassungen vergleichen",
  summary: "Herausfinden, was sich geändert hat – auch ohne Nachverfolgung.",
  durationMinutes: 7,
  learningGoals: [
    "Zwei Dokumentfassungen vergleichen lassen",
    "Den Unterschied zwischen Vergleichen und Zusammenführen kennen",
    "Mit Rückmeldungen ohne Nachverfolgung umgehen",
  ],
  steps: [
    {
      id: "vergleichen",
      title: "Der Vergleich",
      explanation: [
        "In der Praxis passiert es ständig: Jemand überarbeitet ein Dokument, ohne die " +
          "Nachverfolgung einzuschalten, und schickt es zurück. Zwei Dateien, irgendwo " +
          "darin Unterschiede – und keine Markierung, die sie zeigt.",
        "Word kann das nachträglich auflösen. Unter „Überprüfen“ → „Vergleichen“ → " +
          "„Vergleichen…“ wählst du das Originaldokument und die überarbeitete Fassung " +
          "aus. Word erzeugt daraus ein drittes, neues Dokument, in dem alle Unterschiede " +
          "als nachverfolgte Änderungen markiert sind.",
        "Das Ergebnis behandelst du dann genau wie in der letzten Lektion: durchgehen, " +
          "annehmen oder ablehnen. Deine beiden Ausgangsdateien bleiben dabei unberührt.",
      ],
      keyPoints: [
        "Überprüfen → Vergleichen → Vergleichen…",
        "Das Ergebnis ist ein neues Dokument, die Originale bleiben unverändert.",
        "Unterschiede erscheinen als ganz normale nachverfolgte Änderungen.",
      ],
      task: {
        instruction:
          "Speichere zwei leicht unterschiedliche Fassungen eines Textes, vergleiche sie " +
          "über „Überprüfen“ → „Vergleichen“ und öffne im Vergleichsdokument dieses " +
          "Kurs-Panel, um das Ergebnis prüfen zu lassen.",
        hint:
          "Erst Fassung A speichern, ein paar Wörter ändern, als Fassung B speichern. Dann " +
          "Überprüfen → Vergleichen → Vergleichen …, beide Dateien wählen, OK.",
        check: async () => {
          const offen = await countTrackedChanges();
          if (offen === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in keine nachverfolgten Änderungen zur Verfügung (ab WordApi 1.6).",
              "Im Vergleichsdokument sind die Unterschiede farbig markiert – prüfe das selbst. Danach kannst du den Schritt selbst als erledigt markieren.",
            );
          }
          return fromCriteria([criterion(`Markierte Unterschiede im Dokument: ${offen}`, offen > 0)], {
            success: "Die Unterschiede sind sichtbar – jetzt wie gewohnt annehmen oder ablehnen.",
            failure: "In diesem Dokument sind keine Unterschiede markiert.",
            hint: "Das Panel muss im neuen Vergleichsdokument geöffnet sein, nicht in einer der beiden Fassungen.",
          });
        },
      },
    },
    {
      id: "zusammenfuehren",
      title: "Vergleichen oder Zusammenführen?",
      explanation: [
        "Im selben Menü steht „Kombinieren“ – auf den ersten Blick dasselbe, tatsächlich " +
          "aber für einen anderen Fall gedacht.",
        "„Vergleichen“ nimmt zwei Fassungen und zeigt die Unterschiede. „Kombinieren“ " +
          "führt die Überarbeitungen mehrerer Personen in einem Dokument zusammen und " +
          "behält dabei die Zuordnung, wer was geändert hat.",
        "Die Faustregel: Zwei Fassungen und du willst wissen, was anders ist? " +
          "Vergleichen. Drei Rückläufer von drei Personen, die du in einer Fassung " +
          "zusammenbringen willst? Kombinieren.",
        "Wenn ihr regelmäßig gemeinsam an Dokumenten arbeitet, ist beides allerdings nur " +
          "die Notlösung. Liegt die Datei auf OneDrive oder SharePoint, arbeiten alle " +
          "gleichzeitig in derselben Fassung – dann entstehen die abweichenden Kopien " +
          "gar nicht erst.",
      ],
      keyPoints: [
        "Vergleichen: zwei Fassungen, Unterschiede sichtbar machen.",
        "Kombinieren: mehrere Überarbeitungen zusammenführen, Urheber bleiben erkennbar.",
        "Gemeinsame Ablage auf OneDrive oder SharePoint vermeidet das Problem im Vorfeld.",
      ],
    },
  ],
};
